// MobileProfile — tela de PERFIL / AJUSTES da superfície MOBILE (parede A7).
//
// Último placeholder do app mobile vira tela real. Reusa TUDO que já existe; não
// recria store/sync/perfis/i18n:
//  - CONTA: api.getSwipeAccount() (GET /v1/swipe-account/me) mostra o email; o
//    botão Sair chama onLogout (o MobileRoot encerra a sessão Swipe via
//    endSwipeSync + session.logout e cai no MobileAuthFlow — sem estado órfão).
//  - PERFIS: getProfilesForAccount / createProfile / updateProfile / deleteProfile
//    (local-db) + setActiveProfile (store) + syncPull (sync) — o MESMO mecanismo
//    da ProfilesScreen TV. createProfile/updateProfile enfileiram upsert remoto
//    (swipe_profiles, LWW) e deleteProfile enfileira TOMBSTONE (enqueueProfileDelete)
//    — a exclusão propaga pro servidor, não fica só local. A chave de conta vem
//    do STORE, hidratado pela fonte da conta (mobile-sync-bootstrap.ts); a lista
//    vem do pull dos perfis da conta (profiles-load.ts) e "novo perfil" só aparece
//    depois dele (profiles-gate.ts).
//  - FONTE: api.getSwipePreference() mostra a fonte ativa; "trocar/configurar"
//    reabre o MobileConfigureSource (A6) via onConfigureSource.
//  - IDIOMA: setLocale (i18n) — os 3 locales.
//  - SOBRE: versão do app (__APP_VERSION__).
//
// EDITAR/EXCLUIR (parede perfil-editar-excluir): a lista de perfis ganha um modo
// "Gerenciar" (toggle no título, igual em espírito ao manage da ProfilesScreen TV;
// padrão touch mais limpo que long-press — affordance explícita, sem toque
// acidental). Em manage cada perfil mostra Editar (reabre o form inline já
// existente, agora também serve edição) e Excluir (folha de confirmação
// destrutiva, nunca deleta no 1º toque). REGRAS replicadas da TV:
//  - não deixar a conta sem nenhum perfil: o ÚLTIMO perfil não pode ser excluído
//    (canDeleteProfile) — a UI avisa em vez de zerar.
//  - excluir o perfil ATIVO: troca automaticamente pro próximo restante
//    (nextActiveAfterDelete) pra activeProfileId nunca apontar pra perfil morto.
//
// ISOLAMENTO mobile: touch puro (tap/scroll nativo), sem Norigin/foco de remoto.
// Branding: cor de destaque SÓ via --brand-primary (sem hex hardcoded).

import { setLocale, useTranslation } from '@app-iptv/i18n'
import type { Locale } from '@app-iptv/i18n'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { api, ApiError } from '../api'
import {
  createProfile,
  deleteProfile,
  MAX_PROFILES,
  type Profile,
  setProfileHasPin,
  updateProfile,
} from '../local-db'
import { decideProfilesView, type ProfilesPullState } from '../profiles-gate'
import { type AccountProfilesLoad, loadAccountProfiles } from '../profiles-load'
import { accountKey as accountKeyOf, useSessionStore } from '../store'
import {
  confirmPinRecoverRemote,
  removeProfilePinRemote,
  requestPinRecoverRemote,
  setProfilePinRemote,
  syncPull,
  verifyProfilePinRemote,
} from '../sync'

import {
  canDeleteProfile,
  nextActiveAfterDelete,
  normalizeLocale,
  PROFILE_LOCALES,
  PROFILE_NAME_MAX,
  sourceLabel,
  validateProfileName,
} from './mobile-profile'
import { MobilePinEntry } from './MobilePinEntry'
import styles from './MobileProfile.module.css'

export interface MobileProfileProps {
  /** Encerra a sessão Swipe e volta pro login (tratado pelo MobileRoot:
   *  endSwipeSync + session.logout → MobileAuthFlow, sem estado órfão). */
  onLogout: () => void
  /** Reabre o fluxo de configurar/trocar fonte (MobileConfigureSource, A6). */
  onConfigureSource: () => void
}

/** True se o erro da query é o 404 preference_not_set (conta sem fonte). */
function isNoPreference(err: unknown): boolean {
  return err instanceof ApiError && (err.code === 'preference_not_set' || err.code === 'no_provider')
}

/** Estado do form inline: fechado, criando, ou editando um perfil existente. */
type FormState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; profile: Profile }

export function MobileProfile({ onLogout, onConfigureSource }: MobileProfileProps): JSX.Element {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()

  const activeProfileId = useSessionStore((s) => s.activeProfileId)
  const setActiveProfile = useSessionStore((s) => s.setActiveProfile)

  const [locale, setLocaleState] = useState<Locale>(() => normalizeLocale(i18n.language))
  const [confirmLogout, setConfirmLogout] = useState(false)
  // Modo "gerenciar" da lista de perfis (mostra Editar/Excluir por perfil).
  const [managing, setManaging] = useState(false)
  // Form inline compartilhado por criar e editar (1 só UI).
  const [form, setForm] = useState<FormState>({ mode: 'closed' })
  const [formName, setFormName] = useState('')
  const [formKind, setFormKind] = useState<'adult' | 'kids'>('adult')
  const [formError, setFormError] = useState<string | null>(null)
  // Folha de confirmação de exclusão (null = fechada). Destrutivo: nunca apaga
  // no 1º toque — abre a folha primeiro (mesma convenção do confirm de logout
  // e do Modal de remove da ProfilesScreen TV).
  const [confirmDelete, setConfirmDelete] = useState<Profile | null>(null)
  // Aviso de "não dá pra excluir o último perfil" (regra da conta nunca ficar
  // sem perfil). Guarda o nome só pra contexto visual.
  const [lastBlocked, setLastBlocked] = useState(false)

  // ===== PIN por perfil (docs/plan/pin-opcional-perfil.md §6.2) =====
  // Folha de AÇÕES de PIN do perfil (proteger/trocar/remover), aberta pelo botão
  // de cadeado no modo gerenciar. null = fechada.
  const [pinMenuFor, setPinMenuFor] = useState<Profile | null>(null)
  // Overlay de DEFINIR PIN (digite + confirme). null = fechado.
  const [setPinFor, setSetPinFor] = useState<Profile | null>(null)
  const [setPinError, setSetPinError] = useState<string | null>(null)
  const [setPinResetSignal, setSetPinResetSignal] = useState(0)
  const [setPinBusy, setSetPinBusy] = useState(false)
  // Overlay de INSERIR PIN ao trocar pra um perfil protegido. null = fechado.
  const [verifyPinFor, setVerifyPinFor] = useState<Profile | null>(null)
  const [verifyPinError, setVerifyPinError] = useState<string | null>(null)
  const [verifyPinResetSignal, setVerifyPinResetSignal] = useState(0)
  const [verifyPinBusy, setVerifyPinBusy] = useState(false)

  // ===== Conta Swipe (email) ===== //
  const accountQ = useQuery({
    queryKey: ['swipe-account-me'],
    queryFn: () => api.getSwipeAccount(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  // ===== Fonte ativa (preferência sem senha) ===== //
  const prefQ = useQuery({
    queryKey: ['swipe-preference'],
    queryFn: () => api.getSwipePreference(),
    staleTime: 5 * 60 * 1000,
    // 404 preference_not_set não é recuperável por retry (conta só não tem fonte).
    retry: (count, err) => !isNoPreference(err) && count < 1,
  })

  // A chave de conta vem do STORE, hidratado pela fonte da conta
  // (mobile-sync-bootstrap.ts → account-context.ts), a MESMA da TV e do sync. Antes ela
  // era derivada da preferência aqui, só pra esta tela, e nula na fonte manual; o resto
  // do app (escopo, puller) seguia sem contexto.
  const accountKey = useSessionStore((s) => accountKeyOf(s))

  // ===== Perfis da conta ===== //
  // Pull dos perfis da CONTA + leitura local (profiles-load.ts, o mesmo da TV). Antes
  // era só a leitura local: o celular via lista vazia e criava um perfil duplicado.
  const profilesQ = useQuery({
    queryKey: ['mobile-profiles', accountKey],
    queryFn: () => loadAccountProfiles(accountKey as string),
    enabled: accountKey !== null,
    staleTime: 60 * 1000,
  })
  const profiles: Profile[] = profilesQ.data?.profiles ?? []
  // Erro da carga (leitura local falhou) conta como pull falho: "tentar novamente",
  // nunca "carregando" pra sempre.
  const pull: ProfilesPullState = profilesQ.data
    ? profilesQ.data.pull
    : profilesQ.isError
      ? 'failed'
      : 'pending'
  // "Novo perfil" só depois do pull da conta confirmado (profiles-gate.ts).
  const canCreateMore =
    accountKey !== null &&
    decideProfilesView({
      profileCount: profilesQ.data ? profiles.length : null,
      pull,
      userMode: 'list',
      maxProfiles: MAX_PROFILES,
      accountProfileCount: profilesQ.data?.remoteIds ? profilesQ.data.remoteIds.size : null,
    }).canCreate

  /** Atualiza um perfil no cache da query (PIN definido/removido) sem refazer o pull. */
  const patchProfile = (id: string, patch: Partial<Profile>): void => {
    queryClient.setQueryData<AccountProfilesLoad>(['mobile-profiles', accountKey], (cur) =>
      cur ? { ...cur, profiles: cur.profiles.map((x) => (x.id === id ? { ...x, ...patch } : x)) } : cur,
    )
  }

  // Entra de fato no perfil (sem checar PIN). Chamado direto pra perfis sem PIN
  // e pelo fluxo de verify após o PIN correto.
  const enterProfile = (p: Pick<Profile, 'id' | 'kind'>): void => {
    setActiveProfile({ id: p.id, kind: p.kind })
    // Puxa o delta do perfil ("continuar de onde parou" vindo de outro device).
    // Best-effort, não bloqueia. Invalida queries do catálogo scopadas por perfil.
    void syncPull()
    void queryClient.invalidateQueries()
  }

  const switchProfile = (p: Profile): void => {
    // Perfil com PIN → pede o PIN ANTES de entrar (overlay inescapável). Perfil
    // SEM PIN entra direto (fluxo exatamente como antes, sem regressão).
    if (p.hasPin) {
      setVerifyPinError(null)
      setVerifyPinBusy(false)
      setVerifyPinFor(p)
      return
    }
    enterProfile(p)
  }

  // --- DEFINIR/TROCAR PIN (overlay 'set': digite + confirme) ---
  const submitSetPin = async (pin: string): Promise<void> => {
    const target = setPinFor
    if (!target) return
    setSetPinBusy(true)
    setSetPinError(null)
    const res = await setProfilePinRemote(target.id, pin)
    setSetPinBusy(false)
    if (res.ok) {
      await setProfileHasPin(target.id, true)
      patchProfile(target.id, { hasPin: true })
      setSetPinFor(null)
      return
    }
    // Erro do backend (PIN fraco / formato) → mostra e limpa o campo pra nova tentativa.
    setSetPinError(
      res.error === 'pin_too_weak'
        ? t('pin.tooWeak')
        : res.error === 'pin_invalid_format'
          ? t('pin.invalidFormat')
          : t('pin.networkError'),
    )
    setSetPinResetSignal((n) => n + 1)
  }

  // --- REMOVER PIN ---
  const doRemovePin = async (p: Profile): Promise<void> => {
    setPinMenuFor(null)
    const ok = await removeProfilePinRemote(p.id)
    if (ok) {
      await setProfileHasPin(p.id, false)
      patchProfile(p.id, { hasPin: false })
    }
  }

  // --- INSERIR PIN ao entrar (overlay 'verify') ---
  const submitVerifyPin = async (pin: string): Promise<void> => {
    const target = verifyPinFor
    if (!target) return
    setVerifyPinBusy(true)
    setVerifyPinError(null)
    const res = await verifyProfilePinRemote(target.id, pin)
    setVerifyPinBusy(false)
    if (res.status === 'ok' || res.status === 'no_pin') {
      // 'no_pin' (409): o PIN foi removido em outro device; entra sem barrar.
      if (res.status === 'no_pin') {
        await setProfileHasPin(target.id, false)
        patchProfile(target.id, { hasPin: false })
      }
      setVerifyPinFor(null)
      enterProfile(target)
      return
    }
    setVerifyPinError(
      res.status === 'incorrect'
        ? t('pin.incorrect')
        : res.status === 'locked'
          ? t('pin.locked')
          : t('pin.networkError'),
    )
    setVerifyPinResetSignal((n) => n + 1)
  }

  // --- RECUPERAÇÃO por email (do overlay de verify) ---
  const requestRecover = async (): Promise<boolean> => {
    const target = verifyPinFor
    if (!target) return false
    return requestPinRecoverRemote(target.id)
  }

  const confirmRecover = (code: string): ReturnType<typeof confirmPinRecoverRemote> => {
    const target = verifyPinFor
    if (!target) return Promise.resolve({ status: 'network' as const })
    return confirmPinRecoverRemote(target.id, code)
  }

  // Recuperação concluída: o PIN foi removido no backend. Reflete localmente e
  // ENTRA no perfil (a sessão da conta já é a autoridade; sem PIN não barra).
  const onRecovered = (): void => {
    const target = verifyPinFor
    if (!target) return
    void setProfileHasPin(target.id, false)
    patchProfile(target.id, { hasPin: false })
    setVerifyPinFor(null)
    enterProfile(target)
  }

  // Abre o overlay de definir/trocar PIN a partir da folha de ações.
  const openSetPin = (p: Profile): void => {
    setPinMenuFor(null)
    setSetPinError(null)
    setSetPinBusy(false)
    setSetPinFor(p)
  }

  const openCreate = (): void => {
    setForm({ mode: 'create' })
    setFormName('')
    setFormKind('adult')
    setFormError(null)
  }

  const openEdit = (p: Profile): void => {
    setForm({ mode: 'edit', profile: p })
    setFormName(p.name)
    setFormKind(p.kind)
    setFormError(null)
  }

  const closeForm = (): void => {
    setForm({ mode: 'closed' })
    setFormName('')
    setFormKind('adult')
    setFormError(null)
  }

  const submitForm = async (): Promise<void> => {
    if (!accountKey || form.mode === 'closed') return
    const result = validateProfileName(formName)
    if ('error' in result) {
      setFormError(t(result.error))
      return
    }
    try {
      if (form.mode === 'create') {
        const profile = await createProfile(accountKey, {
          name: result.name,
          avatar: '👤',
          kind: formKind,
        })
        await queryClient.invalidateQueries({ queryKey: ['mobile-profiles', accountKey] })
        // Entra direto no perfil novo (consistente com a ProfilesScreen TV).
        setActiveProfile({ id: profile.id, kind: profile.kind })
        void syncPull()
      } else {
        // Edição: updateProfile faz patch local + enqueueProfileUpsert (LWW).
        await updateProfile(form.profile.id, { name: result.name, kind: formKind })
        await queryClient.invalidateQueries({ queryKey: ['mobile-profiles', accountKey] })
        // Se o perfil editado é o ativo, reflete o kind novo no store (kids/adult
        // controla o catálogo). O id não muda.
        if (form.profile.id === activeProfileId) {
          setActiveProfile({ id: form.profile.id, kind: formKind })
        }
      }
      closeForm()
    } catch (e) {
      const code = (e as Error).message
      setFormError(
        code === 'max_profiles'
          ? t('profiles.maxReached', { max: MAX_PROFILES })
          : t('errors.unknown'),
      )
    }
  }

  const requestDelete = (p: Profile): void => {
    // Regra: a conta nunca fica sem nenhum perfil — o último é bloqueado.
    if (!canDeleteProfile(profiles.length)) {
      setLastBlocked(true)
      return
    }
    setConfirmDelete(p)
  }

  const doDelete = async (p: Profile): Promise<void> => {
    if (!accountKey) return
    setConfirmDelete(null)
    // Se o perfil excluído é o ATIVO, decide pra quem trocar ANTES de remover
    // (a lista atual ainda tem todos os perfis).
    const next = nextActiveAfterDelete(profiles, p.id, activeProfileId)
    // deleteProfile: remove local + limpa dados scopados + enqueueProfileDelete
    // (TOMBSTONE → o servidor remove o perfil também, não fica só local).
    await deleteProfile(p.id)
    if (next) {
      setActiveProfile({ id: next.id, kind: next.kind })
      void syncPull()
    }
    await queryClient.invalidateQueries({ queryKey: ['mobile-profiles', accountKey] })
  }

  const changeLocale = (l: Locale): void => {
    if (l === locale) return
    void setLocale(l).then(() => setLocaleState(l))
  }

  const srcLabel = sourceLabel(prefQ.data)
  const formOpen = form.mode !== 'closed'

  return (
    <div className={styles.screen} data-screen="profile">
      {/* ===== Conta ===== */}
      <section className={styles.section} data-section="account">
        <h2 className={styles.sectionTitle}>{t('mobileProfile.accountTitle')}</h2>
        <div className={styles.card}>
          <div className={styles.accountRow}>
            <span className={styles.accountEmail}>
              {accountQ.data?.email ?? (accountQ.isError ? '—' : t('mobileProfile.loading'))}
            </span>
            {accountQ.data?.name && (
              <span className={styles.accountMeta}>{accountQ.data.name}</span>
            )}
          </div>
        </div>
      </section>

      {/* ===== Perfis ===== */}
      <section className={styles.section} data-section="profiles">
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{t('mobileProfile.profilesTitle')}</h2>
          {accountKey !== null && profiles.length > 0 && !formOpen && (
            <button
              type="button"
              className={styles.manageToggle}
              data-action="manage-toggle"
              data-managing={managing ? 'true' : 'false'}
              onClick={() => setManaging((m) => !m)}
            >
              {managing ? t('mobileProfile.manageDone') : t('mobileProfile.manage')}
            </button>
          )}
        </div>

        {accountKey === null ? (
          <p className={styles.hint}>{t('mobileProfile.profilesNeedsSource')}</p>
        ) : pull === 'pending' ? (
          // 1º pull dos perfis da conta em voo: nada de "novo perfil" ainda (seria o
          // duplicado); só o aviso de carregando.
          <p className={styles.hint} data-state="profiles-loading">
            {t('mobileProfile.loading')}
          </p>
        ) : (
          <>
            {pull === 'failed' && (
              // O pull falhou: não dá pra afirmar que a conta não tem perfis. Sem
              // "novo perfil"; "Tentar novamente" refaz o pull. Os perfis já neste
              // aparelho seguem tocáveis abaixo.
              <div className={styles.card} data-state="profiles-failed" role="alert">
                <p className={styles.hint}>{t('empty.loadFailedTitle')}</p>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  data-action="profiles-retry"
                  onClick={() => void profilesQ.refetch()}
                >
                  {t('common.retry')}
                </button>
              </div>
            )}
            <div className={styles.profileList}>
              {profiles.map((p) => {
                const active = p.id === activeProfileId
                if (managing) {
                  // Modo gerenciar: linha com avatar/nome + ações Editar/Excluir
                  // (ambas >= 44px). Não troca de perfil ao tocar a linha.
                  return (
                    <div
                      key={p.id}
                      className={styles.manageRow}
                      data-profile={p.id}
                      data-active={active ? 'true' : 'false'}
                    >
                      <span
                        className={styles.profileAvatar}
                        data-kids={p.kind === 'kids' ? 'true' : 'false'}
                        aria-hidden="true"
                      >
                        {p.avatar}
                      </span>
                      <span className={styles.manageName}>
                        {p.name}
                        {p.kind === 'kids' && (
                          <span className={styles.profileKids}>{t('profiles.kids')}</span>
                        )}
                        {p.hasPin && (
                          <span
                            className={styles.lockBadge}
                            role="img"
                            aria-label={t('pin.protectedAria')}
                            title={t('pin.protectedAria')}
                          >
                            🔒
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        className={styles.iconBtn}
                        data-action="pin-profile"
                        data-haspin={p.hasPin ? 'true' : 'false'}
                        aria-label={p.hasPin ? t('pin.change') : t('pin.protect')}
                        onClick={() => setPinMenuFor(p)}
                      >
                        🔒
                      </button>
                      <button
                        type="button"
                        className={styles.iconBtn}
                        data-action="edit-profile"
                        aria-label={t('mobileProfile.editProfileAria', { name: p.name })}
                        onClick={() => openEdit(p)}
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className={styles.iconBtnDanger}
                        data-action="delete-profile"
                        aria-label={t('mobileProfile.deleteProfileAria', { name: p.name })}
                        onClick={() => requestDelete(p)}
                      >
                        ✕
                      </button>
                    </div>
                  )
                }
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={styles.profileChip}
                    data-profile={p.id}
                    data-active={active ? 'true' : 'false'}
                    aria-pressed={active}
                    onClick={() => switchProfile(p)}
                  >
                    <span
                      className={styles.profileAvatar}
                      data-kids={p.kind === 'kids' ? 'true' : 'false'}
                      aria-hidden="true"
                    >
                      {p.avatar}
                    </span>
                    <span className={styles.profileName}>
                      {p.name}
                      {p.kind === 'kids' && (
                        <span className={styles.profileKids}>{t('profiles.kids')}</span>
                      )}
                      {p.hasPin && (
                        <span
                          className={styles.lockBadge}
                          role="img"
                          aria-label={t('pin.protectedAria')}
                          title={t('pin.protectedAria')}
                        >
                          🔒
                        </span>
                      )}
                    </span>
                    {active && (
                      <span className={styles.profileCheck} aria-hidden="true">
                        ✓
                      </span>
                    )}
                  </button>
                )
              })}

              {canCreateMore && !formOpen && !managing && (
                <button
                  type="button"
                  className={styles.profileAdd}
                  data-action="new-profile"
                  onClick={openCreate}
                >
                  + {t('profiles.new')}
                </button>
              )}
            </div>

            {formOpen && (
              <div
                className={styles.newForm}
                data-form={form.mode === 'edit' ? 'edit-profile' : 'new-profile'}
              >
                {form.mode === 'edit' && (
                  <p className={styles.formTitle}>{t('mobileProfile.editTitle')}</p>
                )}
                <input
                  className={styles.input}
                  type="text"
                  inputMode="text"
                  maxLength={PROFILE_NAME_MAX}
                  placeholder={t('profiles.namePlaceholder')}
                  value={formName}
                  aria-label={t('profiles.nameLabel')}
                  onChange={(e) => setFormName(e.target.value)}
                />
                <div className={styles.kindRow}>
                  <button
                    type="button"
                    className={styles.kindBtn}
                    data-active={formKind === 'adult' ? 'true' : 'false'}
                    onClick={() => setFormKind('adult')}
                  >
                    {t('profiles.adult')}
                  </button>
                  <button
                    type="button"
                    className={styles.kindBtn}
                    data-active={formKind === 'kids' ? 'true' : 'false'}
                    onClick={() => setFormKind('kids')}
                  >
                    {t('profiles.kids')}
                  </button>
                </div>
                {formError && (
                  <p className={styles.error} role="alert">
                    {formError}
                  </p>
                )}
                <div className={styles.formActions}>
                  <button type="button" className={styles.btnGhost} onClick={closeForm}>
                    {t('common.cancelAction')}
                  </button>
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    data-action="save-profile"
                    onClick={() => void submitForm()}
                  >
                    {t('profiles.save')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* ===== Fonte ativa ===== */}
      <section className={styles.section} data-section="source">
        <h2 className={styles.sectionTitle}>{t('mobileProfile.sourceTitle')}</h2>
        <button
          type="button"
          className={styles.actionRow}
          data-action="configure-source"
          onClick={onConfigureSource}
        >
          <span className={styles.actionLabel}>
            <span>{srcLabel ? t('mobileProfile.sourceChange') : t('mobileProfile.sourceAdd')}</span>
            <span className={styles.actionValue}>
              {srcLabel ?? t('mobileProfile.sourceNone')}
            </span>
          </span>
          <span className={styles.actionChevron} aria-hidden="true">
            ›
          </span>
        </button>
      </section>

      {/* ===== Idioma ===== */}
      <section className={styles.section} data-section="language">
        <h2 className={styles.sectionTitle}>{t('settings.language')}</h2>
        {PROFILE_LOCALES.map((l) => {
          const active = l.value === locale
          return (
            <button
              key={l.value}
              type="button"
              className={styles.actionRow}
              data-locale={l.value}
              data-active={active ? 'true' : 'false'}
              aria-pressed={active}
              onClick={() => changeLocale(l.value)}
            >
              <span className={styles.actionLabel}>{l.label}</span>
              {active && (
                <span className={styles.actionCheck} aria-hidden="true">
                  ✓
                </span>
              )}
            </button>
          )
        })}
      </section>

      {/* ===== Sobre ===== */}
      <section className={styles.section} data-section="about">
        <h2 className={styles.sectionTitle}>{t('mobileProfile.aboutTitle')}</h2>
        <div className={styles.card}>
          <div className={styles.aboutRow}>
            <span className={styles.aboutLabel}>{t('settings.about.appVersion')}</span>
            <span className={styles.aboutValue}>{__APP_VERSION__}</span>
          </div>
        </div>
      </section>

      {/* ===== Sair ===== */}
      <section className={styles.section} data-section="logout">
        <button
          type="button"
          className={styles.logoutBtn}
          data-action="logout"
          onClick={() => setConfirmLogout(true)}
        >
          {t('settings.logout')}
        </button>
      </section>

      {/* ===== Folha de confirmação de exclusão de perfil ===== */}
      {confirmDelete && (
        <div
          className={styles.sheetBackdrop}
          role="dialog"
          aria-modal="true"
          aria-label={t('profiles.removeConfirmTitle')}
          onClick={() => setConfirmDelete(null)}
        >
          <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.sheetTitle}>{t('profiles.removeConfirmTitle')}</h3>
            <p className={styles.sheetText}>
              {t('profiles.removeConfirmBody', { name: confirmDelete.name })}
            </p>
            <div className={styles.sheetActions}>
              <button
                type="button"
                className={styles.btnDanger}
                data-action="delete-confirm"
                onClick={() => void doDelete(confirmDelete)}
              >
                {t('profiles.remove')}
              </button>
              <button
                type="button"
                className={styles.btnGhost}
                data-action="delete-cancel"
                onClick={() => setConfirmDelete(null)}
              >
                {t('common.cancelAction')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Folha de aviso: não dá pra excluir o último perfil ===== */}
      {lastBlocked && (
        <div
          className={styles.sheetBackdrop}
          role="dialog"
          aria-modal="true"
          aria-label={t('profiles.removeConfirmTitle')}
          onClick={() => setLastBlocked(false)}
        >
          <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.sheetTitle}>{t('profiles.removeConfirmTitle')}</h3>
            <p className={styles.sheetText} data-hint="last-blocked">
              {t('mobileProfile.deleteLastBlocked')}
            </p>
            <div className={styles.sheetActions}>
              <button
                type="button"
                className={styles.btnGhost}
                data-action="last-blocked-ok"
                onClick={() => setLastBlocked(false)}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Folha de confirmação de logout ===== */}
      {confirmLogout && (
        <div
          className={styles.sheetBackdrop}
          role="dialog"
          aria-modal="true"
          aria-label={t('settings.logout')}
          onClick={() => setConfirmLogout(false)}
        >
          <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.sheetTitle}>{t('settings.logout')}</h3>
            <p className={styles.sheetText}>{t('settings.logoutConfirm')}</p>
            <div className={styles.sheetActions}>
              <button
                type="button"
                className={styles.btnDanger}
                data-action="logout-confirm"
                onClick={() => {
                  setConfirmLogout(false)
                  onLogout()
                }}
              >
                {t('settings.logout')}
              </button>
              <button
                type="button"
                className={styles.btnGhost}
                data-action="logout-cancel"
                onClick={() => setConfirmLogout(false)}
              >
                {t('common.cancelAction')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Folha de AÇÕES de PIN (proteger/trocar/remover) ===== */}
      {pinMenuFor && (
        <div
          className={styles.sheetBackdrop}
          role="dialog"
          aria-modal="true"
          aria-label={pinMenuFor.hasPin ? t('pin.change') : t('pin.protect')}
          data-overlay="pin-menu"
          onClick={() => setPinMenuFor(null)}
        >
          <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.sheetTitle}>
              {pinMenuFor.hasPin ? t('pin.change') : t('pin.protect')}
            </h3>
            <p className={styles.sheetText}>{pinMenuFor.name}</p>
            <div className={styles.sheetActions}>
              <button
                type="button"
                className={styles.btnPrimary}
                data-action="pin-set"
                onClick={() => openSetPin(pinMenuFor)}
              >
                {pinMenuFor.hasPin ? t('pin.change') : t('pin.protect')}
              </button>
              {pinMenuFor.hasPin && (
                <button
                  type="button"
                  className={styles.btnDanger}
                  data-action="pin-remove"
                  onClick={() => void doRemovePin(pinMenuFor)}
                >
                  {t('pin.remove')}
                </button>
              )}
              <button
                type="button"
                className={styles.btnGhost}
                data-action="pin-menu-cancel"
                onClick={() => setPinMenuFor(null)}
              >
                {t('common.cancelAction')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Overlay DEFINIR/TROCAR PIN (digite + confirme 2x) ===== */}
      {setPinFor && (
        <MobilePinEntry
          purpose="set"
          profileName={setPinFor.name}
          onSubmit={(pin) => void submitSetPin(pin)}
          onCancel={() => {
            setSetPinFor(null)
            setSetPinError(null)
          }}
          error={setPinError}
          resetSignal={setPinResetSignal}
          busy={setPinBusy}
        />
      )}

      {/* ===== Overlay INSERIR PIN ao entrar num perfil protegido ===== */}
      {verifyPinFor && (
        <MobilePinEntry
          purpose="verify"
          profileName={verifyPinFor.name}
          onSubmit={(pin) => void submitVerifyPin(pin)}
          onCancel={() => {
            setVerifyPinFor(null)
            setVerifyPinError(null)
          }}
          error={verifyPinError}
          resetSignal={verifyPinResetSignal}
          busy={verifyPinBusy}
          onRequestRecover={requestRecover}
          onConfirmRecover={confirmRecover}
          onRecovered={onRecovered}
        />
      )}
    </div>
  )
}
